'use strict'

const parser = require('ua-parser-js')
const { union } = require('folktale/adt/union')
const noop_ = require('lodash/noop')
const merge_ = require('lodash/merge')
const fromPairs_ = require('lodash/fromPairs')
const { WixCodeViewerApp } = require('@wix/dbsm-common/src/raven/apps')
const {
  configureForViewerWorker
} = require('@wix/dbsm-common/src/raven/configureForViewerWorker')

const Environment = union('Environment', {
  NotInitialized() {},
  Initialized({ createRavenClient, ravenOptions, user, hostType }) {
    const raven = createRavenClient(WixCodeViewerApp.dsn)
    configureForViewerWorker({
      Raven: raven,
      globalScope: global,
      dsn: WixCodeViewerApp.dsn,
      appName: 'wix-code-viewer-app',
      params: ravenOptions
    })

    raven.setUserContext(user)
    raven.setTagsContext({ hostType })

    return { raven }
  }
})

const ravenHandlerCreator = ({ ravenOptions } = {}) => () => {
  let environment = Environment.NotInitialized()

  const getRaven = () =>
    environment.matchWith({
      Initialized: ({ raven }) => raven,
      NotInitialized: () => {
        throw new Error(
          `You cannot use raven before setting the logger environment.
          Make sure you call logger.init before reporting.`
        )
      }
    })

  const calculateIsBrowserUnsupported = () => {
    try {
      const { os, browser } = parser(self.navigator.userAgent)

      return (
        (os.name === 'iOS' && os.version.startsWith('10.')) ||
        (browser.name === 'Safari' && browser.major === '10')
      )
    } catch (e) {
      return false
    }
  }

  const isBrowserUnsupported = calculateIsBrowserUnsupported()

  const calculateLevel = initialLevel => {
    // if browser is not supported, we don't care about the
    // errors but better to log them for future reference
    if (isBrowserUnsupported) {
      return 'info'
    }

    return initialLevel
  }

  const createReportOptions = ({
    level: initialLevel,
    sessionData,
    options = {}
  }) => {
    const level = calculateLevel(initialLevel)
    return merge_({ level }, { extra: sessionData }, options)
  }

  const getResponseDataFromError = error => {
    try {
      if (error.response) {
        const { headers, status, url } = error.response
        return {
          headers: fromPairs_([...headers.entries()]),
          status,
          url
        }
      }
    } catch (e) {
      return e.stack
    }
  }

  const captureException = ({ raven, error, options, sessionData }) => {
    const extraResponseData = getResponseDataFromError(error)
    const finalOptions = merge_(createReportOptions({ sessionData, options }), {
      extra: {
        responseData: extraResponseData
      }
    })
    raven.captureException(error, finalOptions)
  }

  return {
    init: ({ user, hostType, createRavenClient }) => {
      environment = Environment.Initialized({
        createRavenClient,
        ravenOptions,
        user,
        hostType
      })
    },
    log: logEvent => {
      logEvent.matchWith({
        Breadcrumb: ({ breadcrumb }) => {
          const raven = getRaven()
          raven.captureBreadcrumb(breadcrumb)
        },
        Info: ({ message, options, sessionData }) => {
          const raven = getRaven()
          raven.captureMessage(
            message,
            createReportOptions({ level: 'info', sessionData, options })
          )
        },
        Warn: ({ message, options, sessionData }) => {
          const raven = getRaven()
          raven.captureMessage(
            message,
            createReportOptions({ level: 'warning', sessionData, options })
          )
        },
        Error: ({ error, options, sessionData }) => {
          const raven = getRaven()
          captureException({ raven, error, options, sessionData })
        },
        [union.any]: noop_
      })
    }
  }
}

module.exports.ravenHandlerCreator = ravenHandlerCreator
